import "../../stimulus_uses/use_outer_click.js"

// Fields the dirty-state reveal compares between connect time and current
// state. Restricted to the semantic destination/date/group inputs so that
// unrelated mutations (a Turbo frame injecting pre-checked filters, the
// free-text search-filter box, hidden inputs touched by other controllers)
// don't flip the form to "dirty" and pop the submit button without the user
// having changed anything they'd recognise. Mirrors the per-pane scoping
// added in #28511 for the now-removed auto-submit flow.
const DIRTY_STATE_FIELDS = [
  "destination[name]",
  "destination[id]",
  "destination[type]",
  "location_id",
  "region_id",
  "landing_id",
  "locations[]",
  "checkin",
  "checkout",
  "groups[]",
]

// Panes that auto-submit the form on close when their fields changed (the
// pre-#28463 auto-update flow, now restored only for the property page via
// the autoSubmitPanes value). Destination is intentionally absent — changing
// it reveals the submit button instead of auto-submitting.
const AUTO_SUBMIT_PANE_FIELDS = {
  datepicker: ["checkin", "checkout"],
  groups: ["groups[]"],
}

// The destination subset of DIRTY_STATE_FIELDS (everything that isn't a
// date/group field). On the property page the revealed submit button reads
// "Update" until one of these changes, at which point it switches to "Search"
// (the dirtySubmitLabel value).
const DATE_GROUP_FIELDS = Object.values(AUTO_SUBMIT_PANE_FIELDS).flat()
const DESTINATION_DIRTY_FIELDS = DIRTY_STATE_FIELDS.filter(
  (field) => !DATE_GROUP_FIELDS.includes(field),
)

stimulusApp.register(
  "search--form",
  class extends Stimulus.Controller {
    static outlets = [
      "search--location-form",
      "search--travel-date-picker",
      "search--group-configuration",
    ]

    static targets = ["form", "placeSearch", "submitButton"]

    static values = {
      activePane: { type: String, default: "" },
      activeField: { type: String, default: "" },
      autoRevealSubmit: { type: Boolean, default: false },
      autoSubmitPanes: { type: Array, default: [] },
      dirtySubmitLabel: { type: String, default: "" },
      mobileOverlay: { type: Boolean, default: false },
      scrollToForm: { type: Boolean, default: false },
      paneHashes: { type: Object, default: {} },
      recentSearchesStorageKey: String,
      recentSearchesMaxEntries: { type: Number, default: 10 },
      defaultSubmitPath: String,
    }
    #initialFormState = null
    #defaultSubmitLabel = null
    #pristinePlaceSearchHtml = null
    #resetSubmitTimer = null
    #submitting = false
    #onRequestClose = () => {
      this.closePane()
    }
    #onDatepickerTabChange = (event) => {
      if (this.mobileOverlayValue) return
      if (this.#currentPane() !== "datepicker") return
      const field = event.detail?.field
      if (field !== "checkin" && field !== "checkout") return
      this.activeFieldValue = field
      this.#syncClasses(this.#currentPane(), field)
    }
    #onDestinationChange = () => {
      this.#updateSubmitButtonLabel()
    }
    #onPageShow = (event) => {
      if (event.persisted) this.#resetSubmitButton()
    }
    #onHashChange = () => {
      // A queued hashchange can fire after the form leaves the DOM but before
      // Stimulus's async disconnect() detaches this listener; transitioning then
      // hits missing outlet elements and throws. Bail while disconnected.
      if (!this.element.isConnected) return
      this.#transitionToState(this.#stateFromHash(), {
        source: "hash",
        track: false,
      })
    }
    #pageOffset = 0

    connect() {
      if (this.autoRevealSubmitValue && this.hasSubmitButtonTarget) {
        this.submitButtonTarget.classList.add("hidden")
      }
      this.#initialFormState = this.#captureFormState()
      if (this.hasSubmitButtonTarget) {
        this.#defaultSubmitLabel = this.#submitLabelElement().textContent
      }
      if (this.autoRevealSubmitValue && this.hasPlaceSearchTarget) {
        this.#pristinePlaceSearchHtml = this.placeSearchTarget.innerHTML
      }
      this.element.addEventListener(
        "hb:search-form:close",
        this.#onRequestClose,
      )
      this.element.addEventListener(
        "hb:search-form:datepicker-tab-change",
        this.#onDatepickerTabChange,
      )
      this.element.addEventListener(
        "hb:search-form:destination-change",
        this.#onDestinationChange,
      )
      HB.useOuterClick(this, {
        shouldStart: () => Boolean(this.#currentPane()),
        shouldFinish: () => Boolean(this.#currentPane()),
        onClose: () => this.closePane(),
      })
      window.addEventListener("pageshow", this.#onPageShow)
      window.addEventListener("hashchange", this.#onHashChange)
      this.#onHashChange()
    }

    disconnect() {
      this.element.removeEventListener(
        "hb:search-form:close",
        this.#onRequestClose,
      )
      this.element.removeEventListener(
        "hb:search-form:datepicker-tab-change",
        this.#onDatepickerTabChange,
      )
      this.element.removeEventListener(
        "hb:search-form:destination-change",
        this.#onDestinationChange,
      )
      window.removeEventListener("pageshow", this.#onPageShow)
      window.removeEventListener("hashchange", this.#onHashChange)
      clearTimeout(this.#resetSubmitTimer)
      this.#resetSubmitTimer = null
      this.#syncMobileOverlayState(null, this.#currentPane())
    }

    formTargetConnected(target) {
      this.defaultSubmitPathValue = target.action
    }

    searchLocationFormOutletConnected() {
      this.#syncClasses()
    }

    searchTravelDatePickerOutletConnected() {
      this.#syncClasses()
    }

    searchGroupConfigurationOutletConnected() {
      this.#syncClasses()
    }

    openPane(pane, field = null, options = {}) {
      if (!pane) return
      this.#transitionToState(
        { pane, field },
        {
          source: options.fromHash === true ? "hash" : "ui",
          track: options.track !== false,
        },
      )
    }

    closePane(options = {}) {
      this.#transitionToState(
        { pane: null, field: null },
        {
          source: options.fromHash === true ? "hash" : "ui",
          track: options.track !== false,
        },
      )
    }

    revertUnsavedChanges() {
      if (!this.autoRevealSubmitValue) return
      if (this.#pristinePlaceSearchHtml === null) return
      if (!this.#isFormDirty()) return
      this.placeSearchTarget.innerHTML = this.#pristinePlaceSearchHtml
    }

    prepareToSubmit(event) {
      if (event.defaultPrevented) return
      if (this.#submitting) return

      const isValid = this.searchGroupConfigurationOutlet?.validate?.()
      if (isValid === false) {
        event.preventDefault()
        this.openPane("groups")
        this.searchGroupConfigurationOutlet?.showValidation?.()
        return
      }
      this.#trackSubmit()
      this.closePane()

      if (this.scrollToFormValue) {
        localStorage.setItem("scroll-to-search", true)
      }

      const customSubmitPath = this.searchLocationFormOutlet.submitPathValue
      if (customSubmitPath) {
        this.formTarget.action = customSubmitPath
      } else {
        this.formTarget.action = this.defaultSubmitPathValue
      }

      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.classList.remove("hidden")
        this.submitButtonTarget.classList.add("btn_loading")
        this.submitButtonTarget.disabled = true
        clearTimeout(this.#resetSubmitTimer)
        this.#resetSubmitTimer = setTimeout(
          () => this.#resetSubmitButton(),
          1500,
        )
      }

      event.preventDefault()
      this.#submitting = true
      requestAnimationFrame(() =>
        requestAnimationFrame(() =>
          setTimeout(() => this.formTarget.requestSubmit(), 0),
        ),
      )
    }

    saveSearch(event) {
      if (event.defaultPrevented) return
      if (!this.recentSearchesStorageKeyValue) return

      const search = this.#buildRecentSearch()
      if (!search) return

      const storedSearches = this.#readStoredSearches()
      const uniqueSearches = storedSearches.filter(
        (storedSearch) => !this.#sameSearch(storedSearch, search),
      )
      const updatedSearches = [search, ...uniqueSearches].slice(
        0,
        this.recentSearchesMaxEntriesValue,
      )

      try {
        localStorage.setItem(
          this.recentSearchesStorageKeyValue,
          JSON.stringify(updatedSearches),
        )
      } catch (_error) {
        // noop: do not block form submission when localStorage is unavailable
      }
    }

    togglePane(event) {
      event.preventDefault()
      const element = event.currentTarget
      const pane = element.dataset.pane
      const field = this.#normalizePaneField(
        pane,
        element.dataset.field || null,
      )
      if (this.#currentPane() === pane) {
        if (pane === "datepicker" && field && this.#currentField() !== field) {
          this.openPane(pane, field)
          return
        }
        this.closePane()
        return
      }
      this.openPane(pane, field)
    }

    #openPaneController(pane, field) {
      if (pane === "destinations") {
        this.searchLocationFormOutlet?.open?.()
      }
      if (pane === "datepicker") {
        this.searchTravelDatePickerOutlet?.open?.(field)
      }
      if (pane === "groups") {
        this.searchGroupConfigurationOutlet?.open?.()
      }
    }

    #syncClasses(
      activePane = this.#currentPane(),
      activeField = this.#currentField(),
    ) {
      this.element.classList.toggle(
        "place-search--container_opened",
        Boolean(activePane),
      )
      if (!this.hasPlaceSearchTarget) return
      const paneClasses = [
        "place-search_destinations",
        "place-search_groups",
        "place-search_datepicker-checkin",
        "place-search_datepicker-checkout",
      ]
      this.placeSearchTarget.classList.remove(...paneClasses)
      if (this.mobileOverlayValue) return
      const activeClass = this.#paneClassName(activePane, activeField)
      if (activeClass) {
        this.placeSearchTarget.classList.add(activeClass)
      }
    }

    #paneClassName(pane, field) {
      if (pane === "destinations") return "place-search_destinations"
      if (pane === "groups") return "place-search_groups"
      if (pane === "datepicker") {
        return field === "checkout"
          ? "place-search_datepicker-checkout"
          : "place-search_datepicker-checkin"
      }
      return null
    }

    #normalizePaneField(pane, field) {
      if (
        pane !== "datepicker" ||
        field !== "checkout" ||
        this.mobileOverlayValue
      ) {
        return field
      }
      const canSelectCheckout =
        this.searchTravelDatePickerOutlet?.canSelectCheckout?.()
      if (canSelectCheckout === false) return "checkin"
      return field
    }

    #hashForPane(pane) {
      const paneHashes = this.paneHashesValue || {}
      const value = paneHashes[pane]
      if (typeof value === "string" && value.length > 0) return value
      return pane
    }

    #paneFromHash(rawHash) {
      const normalized = String(rawHash || "").replace(/^#/, "")
      if (!normalized) return null
      const paneHashes = this.paneHashesValue || {}
      const explicitPane = Object.entries(paneHashes).find(
        ([_, hash]) => hash === normalized,
      )?.[0]
      if (explicitPane) return explicitPane
      const knownPanes = ["destinations", "datepicker", "groups"]
      if (knownPanes.includes(normalized)) return normalized
      return null
    }

    #stateFromHash() {
      const pane = this.#paneFromHash(window.location.hash)
      if (!pane) return { pane: null, field: null }
      return { pane, field: null }
    }

    #syncHashToState(state, options = {}) {
      if (!this.mobileOverlayValue || options.source === "hash") return
      const currentHash = String(window.location.hash || "").replace(/^#/, "")
      const nextHash = state.pane ? this.#hashForPane(state.pane) : ""
      if (currentHash === nextHash) return
      if (!nextHash) {
        const url = new URL(window.location.href)
        url.hash = ""
        window.history.replaceState(window.history.state, "", url.toString())
        return
      } else {
        window.location.hash = nextHash
      }
    }

    #syncMobileOverlayState(nextPane, previousPane) {
      if (!this.mobileOverlayValue) return
      const hadOpenPane = Boolean(previousPane)
      const hasOpenPane = Boolean(nextPane)
      if (!hadOpenPane && hasOpenPane) {
        this.#mountMobileOverlay()
        return
      }
      if (hadOpenPane && !hasOpenPane) {
        this.#unmountMobileOverlay()
      }
    }

    #mountMobileOverlay() {
      window.HB.fullscreenOverlay.beforeMount()
      this.#pageOffset = window.pageYOffset
      this.#postToNative("disable_pull_to_refresh")
    }

    #unmountMobileOverlay() {
      window.HB.fullscreenOverlay.beforeUnmount()
      window.scrollTo(0, this.#pageOffset)
      this.#postToNative("enable_pull_to_refresh")
    }

    #postToNative(action) {
      window.ReactNativeWebView?.postMessage?.(JSON.stringify({ action }))
    }

    activePaneValueChanged(value, oldValue) {
      this.#syncMobileOverlayState(value, oldValue)
      this.#syncClasses()
      if (!oldValue && value) {
        this.#dispatchPaneEvent("pane-opened")
      } else if (oldValue && !value) {
        this.#dispatchPaneEvent("pane-closed")
      }
    }

    #dispatchPaneEvent(type) {
      this.element.dispatchEvent(
        new CustomEvent(`hb:search-form:${type}`, { bubbles: true }),
      )
    }

    activeFieldValueChanged() {
      this.#syncClasses()
    }

    #currentPane() {
      return this.activePaneValue || null
    }

    #currentField() {
      return this.activeFieldValue || null
    }

    #currentState() {
      return {
        pane: this.#currentPane(),
        field: this.#currentField(),
      }
    }

    #resetSubmitButton() {
      this.#submitting = false
      if (!this.hasSubmitButtonTarget) return
      this.submitButtonTarget.classList.remove("btn_loading")
      this.submitButtonTarget.disabled = false
      if (this.autoRevealSubmitValue) {
        this.submitButtonTarget.classList.add("hidden")
        this.#updateSubmitButtonVisibility()
      }
      this.#updateSubmitButtonLabel()
    }

    #captureFormState() {
      if (!this.hasFormTarget) return null
      return [
        ...Array.from(new FormData(this.formTarget).entries()).map(
          ([key, value]) => [key, String(value || "").trim()],
        ),
        ["destination[name]", this.#destinationName()],
      ]
    }

    #isFormDirty() {
      if (!this.#initialFormState) return false
      return this.#fieldsDirty(DIRTY_STATE_FIELDS)
    }

    #updateSubmitButtonVisibility() {
      if (!this.autoRevealSubmitValue || !this.hasSubmitButtonTarget) return
      if (this.submitButtonTarget.classList.contains("btn_loading")) return
      this.submitButtonTarget.classList.toggle("hidden", !this.#isFormDirty())
    }

    #updateSubmitButtonLabel() {
      if (!this.dirtySubmitLabelValue || this.#defaultSubmitLabel === null)
        return
      if (this.submitButtonTarget.classList.contains("btn_loading")) return
      this.#submitLabelElement().textContent = this.#fieldsDirty(
        DESTINATION_DIRTY_FIELDS,
      )
        ? this.dirtySubmitLabelValue
        : this.#defaultSubmitLabel
    }

    #submitLabelElement() {
      return (
        this.submitButtonTarget.querySelector(".caps") ||
        this.submitButtonTarget
      )
    }

    #shouldAutoSubmitOnClose(pane) {
      if (!this.mobileOverlayValue || !pane) return false
      if (
        this.hasSubmitButtonTarget &&
        this.submitButtonTarget.classList.contains("btn_loading")
      ) {
        return false
      }
      if (!this.autoSubmitPanesValue.includes(pane)) return false
      if (
        pane === "groups" &&
        this.searchGroupConfigurationOutlet?.validate?.() === false
      ) {
        return false
      }
      const fields = AUTO_SUBMIT_PANE_FIELDS[pane]
      return fields ? this.#fieldsDirty(fields) : false
    }

    #fieldsDirty(fields) {
      const pick = (state) =>
        (state || []).filter(([name]) => fields.includes(name))
      return (
        JSON.stringify(pick(this.#initialFormState)) !==
        JSON.stringify(pick(this.#captureFormState()))
      )
    }

    #submitForm() {
      this.formTarget.requestSubmit(
        this.hasSubmitButtonTarget ? this.submitButtonTarget : undefined,
      )
    }

    #trackSubmit() {
      const search = this.#buildFormMetadata()
      const terms = String(
        search["search-filter"] || search["destination[name]"] || "",
      )
      Activity.track("search-form", "submit", terms.toLowerCase(), terms, {
        search,
      })
    }

    #buildFormMetadata() {
      const metadata = {}
      new FormData(this.formTarget).forEach((value, key) => {
        const stringValue = String(value)
        if (Object.prototype.hasOwnProperty.call(metadata, key)) {
          metadata[key] = Array.isArray(metadata[key])
            ? [...metadata[key], stringValue]
            : [metadata[key], stringValue]
          return
        }
        metadata[key] = stringValue
      })
      metadata["destination[name]"] = this.#destinationName()
      return metadata
    }

    #transitionToState(targetState, options = {}) {
      const currentState = this.#currentState()
      const nextState = this.#normalizeState(targetState, currentState, options)
      if (this.#statesEqual(currentState, nextState)) return

      if (
        options.track !== false &&
        options.source !== "hash" &&
        nextState.pane
      ) {
        Activity.track("search-form", "change", nextState.pane)
      }

      this.#applyState(nextState, currentState)
      this.#syncHashToState(nextState, {
        source: options.source,
      })

      if (!nextState.pane) {
        if (
          options.source !== "hash" &&
          this.#shouldAutoSubmitOnClose(currentState.pane)
        ) {
          this.#submitForm()
        } else {
          this.#updateSubmitButtonVisibility()
          this.#updateSubmitButtonLabel()
        }
      }
    }

    #normalizeState(
      targetState,
      currentState = this.#currentState(),
      options = {},
    ) {
      const pane = targetState?.pane || null
      if (!pane) return { pane: null, field: null }

      let field = this.#normalizePaneField(pane, targetState?.field || null)
      if (pane === "datepicker" && !field) {
        const activeTab =
          currentState.pane === "datepicker" && currentState.field
            ? currentState.field
            : this.searchTravelDatePickerOutlet?.activeTabValue || "checkin"
        field =
          options.source === "hash"
            ? activeTab
            : this.#normalizePaneField(pane, activeTab)
      }
      return { pane, field: field || null }
    }

    #statesEqual(left, right) {
      return left.pane === right.pane && left.field === right.field
    }

    #applyState(nextState, previousState) {
      this.activePaneValue = nextState.pane || ""
      this.activeFieldValue = nextState.field || ""
      this.#syncPaneControllers(nextState, previousState)
    }

    #syncPaneControllers(nextState, previousState) {
      if (previousState.pane && previousState.pane !== nextState.pane) {
        this.#closePaneController(previousState.pane)
      }

      if (!nextState.pane) {
        this.#closePaneController("destinations")
        this.#closePaneController("datepicker")
        this.#closePaneController("groups")
        return
      }

      if (nextState.pane === "datepicker") {
        this.searchTravelDatePickerOutlet.activeTabValue = nextState.field
      }

      this.#openPaneController(nextState.pane, nextState.field)
    }

    #closePaneController(pane) {
      if (pane === "destinations") {
        this.searchLocationFormOutlet?.close?.()
      }
      if (pane === "datepicker") {
        this.searchTravelDatePickerOutlet?.close?.()
      }
      if (pane === "groups") {
        this.searchGroupConfigurationOutlet?.close?.()
      }
    }
    #buildRecentSearch() {
      if (!this.hasFormTarget) return null
      const formData = new FormData(this.formTarget)
      const destination = this.#buildDestination(formData)
      if (!destination?.name) return null

      return {
        "selected-destination": destination,
        checkin: this.#compactIsoDate(formData.get("checkin")),
        checkout: this.#compactIsoDate(formData.get("checkout")),
        groups: this.#parseGroups(formData.getAll("groups[]")),
        timestamp: Date.now(),
      }
    }

    #buildDestination(formData) {
      const destinationType = String(formData.get("destination[type]") || "")
      const destinationName = this.#destinationName()
      const locationId = this.#normalizeId(formData.get("location_id"))
      const regionId = this.#normalizeId(formData.get("region_id"))
      const destinationId = this.#normalizeId(formData.get("destination[id]"))
      const destination = {
        name: destinationName || null,
        "location-id": locationId,
        "region-id": regionId,
        id: destinationId || regionId || locationId,
        "search-type": destinationType === "place" ? "property" : "location",
      }
      const submitPath = this.searchLocationFormOutlet.submitPathValue
      if (submitPath) destination.submitPath = submitPath
      return destination
    }

    #parseGroups(groupValues) {
      return groupValues
        .map((groupValue) => this.#parseGroup(groupValue))
        .filter(Boolean)
    }

    #parseGroup(groupValue) {
      const values = String(groupValue)
        .split(",")
        .map((value) => value.trim())
      const adults = Number.parseInt(values[0], 10)
      if (!Number.isFinite(adults) || adults <= 0) return null

      const childrenAges = values
        .slice(1)
        .filter((age) => age !== "")
        .map((age) => Number.parseInt(age, 10))
        .filter((age) => Number.isFinite(age) && age >= 0)

      return { adults, "children-ages": childrenAges }
    }

    #compactIsoDate(value) {
      const isoDate = String(value || "").trim()
      if (!isoDate) return null
      const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/)
      if (!match) return null
      return `${match[1]}${match[2]}${match[3]}T000000`
    }

    #normalizeId(value) {
      const id = String(value || "").trim()
      return id === "" ? null : id
    }

    #destinationName() {
      if (!this.hasSearchLocationFormOutlet) return ""
      return String(
        this.searchLocationFormOutlet.destinationNameValue || "",
      ).trim()
    }

    #readStoredSearches() {
      try {
        const parsed = JSON.parse(
          localStorage.getItem(this.recentSearchesStorageKeyValue) || "[]",
        )
        return Array.isArray(parsed) ? parsed : []
      } catch (_error) {
        return []
      }
    }

    // TODO: Simplify this logic. All of this is mostly for backwards
    // compatibility with recent searches saved by the old search form.
    #sameSearch(left, right) {
      return this.#normalizeSearch(left) === this.#normalizeSearch(right)
    }

    #normalizeSearch(search) {
      const destination =
        search?.selectedDestination || search?.["selected-destination"] || {}
      const groups = Array.isArray(search?.groups) ? search.groups : []
      const searchType =
        destination?.searchType || destination?.["search-type"] || "location"
      const isProperty = searchType === "property"

      return JSON.stringify({
        selectedDestination: {
          name:
            destination?.name ||
            destination?.locationName ||
            destination?.["location-name"] ||
            "",
          locationId: isProperty
            ? null
            : this.#normalizeComparableId(
                destination?.locationId ?? destination?.["location-id"] ?? null,
              ),
          regionId: isProperty
            ? null
            : this.#normalizeComparableId(
                destination?.regionId ?? destination?.["region-id"] ?? null,
              ),
          destinationId: this.#normalizeComparableId(
            destination?.destinationId ??
              destination?.["destination-id"] ??
              destination?.id ??
              null,
          ),
          searchType,
        },
        checkin: search?.checkin || null,
        checkout: search?.checkout || null,
        groups: groups.map((group) => ({
          adults:
            Number.parseInt(
              group?.adults ??
                group?.["adults-count"] ??
                group?.adults_count ??
                0,
              10,
            ) || 0,
          childrenAges: Array.isArray(
            group?.childrenAges ??
              group?.["children-ages"] ??
              group?.children_ages,
          )
            ? (
                group.childrenAges ||
                group["children-ages"] ||
                group.children_ages
              )
                .map((age) => Number.parseInt(age, 10))
                .filter((age) => Number.isFinite(age) && age >= 0)
            : [],
        })),
      })
    }

    #normalizeComparableId(value) {
      if (value == null) return null
      const normalized = String(value).trim()
      return normalized === "" ? null : normalized
    }
  },
)
